import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Award, Globe, BookOpen, FileText, Newspaper, GraduationCap, Video, Facebook, Youtube } from 'lucide-react';
import Footer from './Footer';

const AboutDeveloper = () => {
  // Schema for the About page
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Diane Shawe",
    "url": "https://www.diane-shawe.co.uk",
    "jobTitle": "Author, Educator, Business Leader",
    "description": "Diane Shawe is an award-winning author, business leader, and creator of the Video Book Review Specialist training program.",
    "sameAs": [
      "https://www.linkedin.com/in/dianeshawe/",
      "https://www.amazon.com/Diane-Shawe/e/B0052WG8V",
      "https://www.goodreads.com/author/show/7522508.Diane_Shawe",
      "https://medium.com/@Needahairmakeover",
      "https://harvard.academia.edu/DianeShaweMEd"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About the Developer - Diane Shawe | Video Book Review Specialist</title>
        <meta name="description" content="Learn about Diane Shawe, award-winning author, business leader, and creator of the Video Book Review Specialist training program. Explore her publications, awards, and global presence." />
        <link rel="canonical" href="https://enrolment.bookreviewspecialists.app/about-developer" />
        <meta property="og:title" content="About the Developer - Diane Shawe" />
        <meta property="og:description" content="Award-winning author and creator of Video Book Review Specialist training." />
        <meta property="og:url" content="https://enrolment.bookreviewspecialists.app/about-developer" />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Video Book Review Specialist Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-slate-800">Video Book Review Specialist</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-slate-600 hover:text-slate-800">Home</Link>
              <Link to="/become-a-video-book-review-specialist" className="text-slate-600 hover:text-slate-800">Training</Link>
              <Link to="/blog-insights" className="text-slate-600 hover:text-slate-800">Blog</Link>
              <Link to="/enroll" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Enrol Now</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Header Section */}
          <section className="mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">About the Developer</h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Explore Diane Shawe's work, publications, and recognised presence across global publishing platforms, professional networks, and media channels.
            </p>
            
            {/* Video - Flushed Left */}
            <div className="mb-8">
              <video 
                controls 
                className="w-full max-w-lg rounded-xl shadow-lg"
                poster=""
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/zsd5edj6_Diane%20shawe%20introduces%20video%20book%20review%20course.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </section>

          {/* Awards & Recognition */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Award className="w-6 h-6 text-amber-500 mr-3" />
              Awards & Recognition
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-semibold text-slate-800 mb-1">Black European Business Women of the Year</h3>
                <p className="text-slate-600 text-sm">Presidents Award (2003)</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-semibold text-slate-800 mb-1">Divas of Colour Awards</h3>
                <p className="text-slate-600 text-sm">Finalist – Author of the Year (2016)</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-semibold text-slate-800 mb-1">StartUp Awards Midlands</h3>
                <p className="text-slate-600 text-sm">Finalist – Mobile & Emerging Technologies Startup of the Year (2023)</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-semibold text-slate-800 mb-1">Women Like Me Business Club</h3>
                <p className="text-slate-600 text-sm">Winner – Featured in Women Like Me Awards Ceremony (2025)</p>
              </div>
            </div>
          </section>

          {/* Business Leadership Recognition */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Award className="w-6 h-6 text-blue-500 mr-3" />
              Business Leadership Recognition
            </h2>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
              <p className="text-slate-700 mb-3">Featured in <strong>Top 20 Business Leaders Setting The Pace for 2025</strong></p>
              <a 
                href="https://share.google/MtBCICfHnO7AXU4VF" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                View feature →
              </a>
            </div>
          </section>

          {/* Global Distribution */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Globe className="w-6 h-6 text-green-500 mr-3" />
              Global Distribution
            </h2>
            <p className="text-slate-600 mb-4">
              Diane Shawe's publications are available internationally through major retail and digital platforms.
            </p>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Amazon (UK & USA)
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Barnes & Noble
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Booktopia (Australia)
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Google Books & digital platforms
              </li>
            </ul>
          </section>

          {/* Verified Presence, Publications & Platforms */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <BookOpen className="w-6 h-6 text-purple-500 mr-3" />
              Verified Presence, Publications & Platforms
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Buy & Access</h3>
            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://www.diane-shawe.co.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Diane Shawe Author Website
                </a>
                <p className="text-slate-500 text-sm mt-1">Official website</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://www.amazon.com/Diane-Shawe/e/B0052WG8V" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Amazon Author Profile
                </a>
                <p className="text-slate-500 text-sm mt-1">eBooks and global author profile available across international Amazon marketplaces</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://play.google.com/store/books/author?id=Diane+Shawe" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Google Play Books
                </a>
                <p className="text-slate-500 text-sm mt-1">Digital publications accessible globally</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://www.goodreads.com/author/show/7522508.Diane_Shawe" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Goodreads
                </a>
                <p className="text-slate-500 text-sm mt-1">Author profile featuring published works, reviews, and reader engagement</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://www.barnesandnoble.com/w/scrolling-shadows-sucked-into-the-darkside-of-instagramverse-diane-shawe/1146998011?ean=9789370181588" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Barnes & Noble
                </a>
                <p className="text-slate-500 text-sm mt-1">Available through one of the largest bookstore retailers in the United States</p>
              </div>
              
              <div className="pb-4">
                <a 
                  href="https://www.booktopia.com.au/scrolling-shadows-sucked-into-the-darkside-of-instagramverse-diane-shawe/book/9789370181588.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Booktopia
                </a>
                <p className="text-slate-500 text-sm mt-1">Available through a leading Australian online bookstore</p>
              </div>
            </div>
          </section>

          {/* Content, Articles & Thought Leadership */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <FileText className="w-6 h-6 text-orange-500 mr-3" />
              Content, Articles & Thought Leadership
            </h2>
            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://medium.com/@Needahairmakeover" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-600 hover:text-orange-800 font-medium"
                >
                  Medium
                </a>
                <p className="text-slate-500 text-sm mt-1">Articles and insights covering business, visibility, and expertise</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://substack.com/@ladydianeshawe" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-600 hover:text-orange-800 font-medium"
                >
                  Substack
                </a>
                <p className="text-slate-500 text-sm mt-1">Long-form writing, updates, and direct audience engagement</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://qualiquiz.app/blog" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-600 hover:text-orange-800 font-medium"
                >
                  QualiQuiz Blog
                </a>
                <p className="text-slate-500 text-sm mt-1">Emerging technologies and business insights</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <Link 
                  to="/blog-insights"
                  className="text-orange-600 hover:text-orange-800 font-medium"
                >
                  Video Book Review Blog
                </Link>
                <p className="text-slate-500 text-sm mt-1">Industry insights on video book reviewing</p>
              </div>
              
              <div className="pb-4">
                <a 
                  href="https://www.needahairmakeover.blog" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-600 hover:text-orange-800 font-medium"
                >
                  Need a Hair Makeover Blog
                </a>
                <p className="text-slate-500 text-sm mt-1">Business and consumer content</p>
              </div>
            </div>
          </section>

          {/* Media & Editorial Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Newspaper className="w-6 h-6 text-red-500 mr-3" />
              Media & Editorial Features
            </h2>
            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://www.worldauthors.org/who-is-the-author-of-the-book-scrolling-shadows/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-800 font-medium"
                >
                  World Authors – Scrolling Shadows Feature
                </a>
                <p className="text-slate-500 text-sm mt-1">Feature article introducing Diane Shawe and her book Scrolling Shadows</p>
              </div>
              
              <div className="pb-4">
                <a 
                  href="https://www.worldauthors.org/sucked-into-the-deception-diane-shawes-thrilling-sequel-to-scrolling-shadows/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-800 font-medium"
                >
                  World Authors – Sucked into the Deception Feature
                </a>
                <p className="text-slate-500 text-sm mt-1">Editorial coverage of Sucked into the Deception, reinforcing author positioning</p>
              </div>
            </div>
          </section>

          {/* Academic & Professional Presence */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <GraduationCap className="w-6 h-6 text-indigo-500 mr-3" />
              Academic & Professional Presence
            </h2>
            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://harvard.academia.edu/DianeShaweMEd" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Academia (Harvard domain)
                </a>
                <p className="text-slate-500 text-sm mt-1">Academic-style profile highlighting research, insights, and professional contributions</p>
              </div>
              
              <div className="border-b border-slate-100 pb-4">
                <a 
                  href="https://www.linkedin.com/in/dianeshawe/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  LinkedIn
                </a>
                <p className="text-slate-500 text-sm mt-1">Professional profile outlining business leadership and activity</p>
              </div>
              
              <div className="pb-4">
                <a 
                  href="https://dianeshawe.brandyourself.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  BrandYourself
                </a>
                <p className="text-slate-500 text-sm mt-1">Verified digital presence supporting reputation and authority</p>
              </div>
            </div>
          </section>

          {/* Platforms Supporting Video Visibility */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Video className="w-6 h-6 text-pink-500 mr-3" />
              Platforms Supporting Video Visibility
            </h2>
            <p className="text-slate-600 mb-8">
              Platforms where video reviews increase visibility and engagement
            </p>
            
            {/* Logo Carousel */}
            <div className="bg-slate-50 rounded-xl p-8">
              <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
                {/* Amazon */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" alt="Amazon" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">Amazon</span>
                </div>
                {/* Goodreads */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/1/1a/Goodreads_logo.svg" alt="Goodreads" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">Goodreads</span>
                </div>
                {/* Medium */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/ec/Medium_logo_Monogram.svg" alt="Medium" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">Medium</span>
                </div>
                {/* YouTube */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b8/YouTube_Logo_2017.svg" alt="YouTube" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">YouTube</span>
                </div>
                {/* TikTok */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/en/a/a9/TikTok_logo.svg" alt="TikTok" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">TikTok</span>
                </div>
                {/* KDP */}
                <div className="flex flex-col items-center">
                  <div className="h-8 flex items-center justify-center text-slate-600 font-bold text-lg opacity-70 hover:opacity-100 transition-opacity">KDP</div>
                  <span className="text-xs text-slate-500 mt-2">Kindle Direct</span>
                </div>
                {/* LinkedIn */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" alt="LinkedIn" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">LinkedIn</span>
                </div>
                {/* Substack */}
                <div className="flex flex-col items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/8/82/Substack.svg" alt="Substack" className="h-8 opacity-70 hover:opacity-100 transition-opacity" />
                  <span className="text-xs text-slate-500 mt-2">Substack</span>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-slate-400 text-center mt-4 italic">
              Logos shown represent platforms and industries where video book reviews are relevant. No direct affiliation implied.
            </p>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Start Your Journey?</h2>
            <p className="text-white/90 mb-6">Join the Video Book Review Specialist training program</p>
            <Link 
              to="/enroll"
              className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Enrol Now from £9.99
            </Link>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutDeveloper;
